import './DataGrid.scss';
import { FC, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { TabsContext } from '@srm/ui/context/TabsContext';
import clsx from 'clsx';
import { HiXMark } from 'react-icons/hi2';
import DataTable, { IDataTableColumn, IDataTableScrollPosition } from '@srm/ui/data-table';
import { DataTableWrapMode, IDataTableHandle } from '@srm/ui/data-table/types';
import DataTableFailure from '@srm/ui/data-table/failure';
import { sendNotification } from '@srm/ui/lib/notifications';
import { errorText, errorTraceId } from '@srm/core/errors';
import { IDataGridService } from '@services/IDataGridService';
import { useUserScripts } from '@hooks/useUserScripts';
import { IDataGridStateService } from '@services/IDataGridStateService';
import { IGridStateSettings } from '@models/response/IGridStateSettings';
import { IDataTableSavedState } from '@srm/ui/data-table/state';
import StateModal from '@organisms/StateModal/StateModal';
import { tableStateToView, viewToTableState } from './dataGridViews';
import { gridStateChanges } from './gridStateChanges';
import { compactGridTableUrlStateBySchema, IGridUrlSchema, restoreGridTableUrlState } from './dataGridUrlState';
import { IGridDescriptionResponse } from '@models/response/IGridDescriptionResponse';
import TableSchemeDebugButton from '@molecules/DebugPanel/TableSchemeDebug/TableSchemeDebugButton';
import {
    ITableSchemeDebug,
    ITableSchemeDetail,
    toSchemeDebugColumns,
} from '@molecules/DebugPanel/TableSchemeDebug/tableSchemeDebug';
import { IDataGridDescriptionResponse } from '@models/response/IDataGridDescriptionResponse';
import DocumentFormDetails from './parts/DocumentFormDetails/DocumentFormDetails';
import GridStateButton from './parts/GridStateButton/GridStateButton';
import { getDocIdFromLinkedDocId } from '@utils/helpers';
import { applyDataGridCellRender, IDataGridCellDeps, linkedDocHrefOf } from './dataGridCells';
import { createDataGridSource } from './dataGridSource';
import type { IServerLoadResult } from '@srm/ui/data-table/server';
import {
    buildDataGridColumns,
    buildDataGridOptions,
    buildDataGridSort,
    buildFieldsParam,
    dataGridColumnId,
    DataGridRow,
} from './dataGridSchema';

function schemeDebugDetails(scheme: IGridDescriptionResponse): ITableSchemeDetail[] {
    const details: ITableSchemeDetail[] = [];
    if (scheme.options?.stateKey) details.push({ label: 'stateKey', value: scheme.options.stateKey });
    details.push({
        label: 'ссылки на документ',
        value: scheme.options?.urlEnabled ? 'включены' : 'выключены',
    });
    return details;
}

/** Ширина колонки действий: помещается кнопка меню строки и ничего лишнего. */
const ACTIONS_COLUMN_WIDTH = 44;

export interface IDataGridProps {
    /** Ключ набора данных (папка поиска, таблица связей, журнал). */
    dataKey: string;
    /** Сервис списка: схема, данные, экспорт (см. IDataGridService). */
    service: IDataGridService;
    /** Готовая схема вместо запроса: папки поиска берут её после выбора
     *  предустановленного фильтра, потому что фильтр меняет состав колонок. */
    scheme?: IGridDescriptionResponse | null;
    /** Донастройка колонок поверх схемных рендеров (свои шаблоны ячеек). */
    columnRender?: (column: IDataTableColumn<DataGridRow>) => IDataTableColumn<DataGridRow>;
    /** Префикс ссылки на документ в ячейках (`/document`). */
    linkPrefix?: string;
    /** Класс переноса текста в ячейках (настройка пользователя). */
    wordWrapClassName?: string;
    /** Режим просмотра: без действий и hover-карточек. */
    readOnly?: boolean;
    /** Шаблоны ячеек по имени из схемы (`dxGridCellTemplate`). */
    cellTemplates?: Record<string, (row: DataGridRow) => ReactNode>;
    /** Кнопки в тулбаре таблицы. */
    toolbarStart?: ReactNode;
    /** Правая часть тулбара (предустановленные фильтры, свои переключатели). */
    toolbarEnd?: ReactNode;
    /**
     * Жучок схемы таблицы. Кнопку рисует сам компонент и только при допуске к панели отладки.
     * Колонки и stateKey берутся из уже загруженной схемы грида.
     */
    schemeDebug?: ITableSchemeDebug;
    /** Правая часть футера под списком (легенда). */
    footerEnd?: ReactNode;
    /** Действия строки. */
    rowActions?: (row: DataGridRow) => ReactNode;
    /** Ширина колонки действий, когда в ней больше одной кнопки. */
    rowActionsWidth?: number;
    /** Клик по строке — открытие записи справочника и т. п. */
    onRowClick?: (row: DataGridRow) => void;
    /** Открывает ссылку документа по клику в свободной области строки. Ссылка
     *  доступна только при `linkPrefix` и разрешённом схемой `urlEnabled`. */
    onOpenRowLink?: (href: string, row: DataGridRow) => void;
    /** Свой класс строки — доменная подсветка. */
    rowClassName?: (row: DataGridRow) => string | undefined;
    /** Цветной корешок строки: ошибка (красный) или предупреждение (жёлтый). */
    rowStatus?: (row: DataGridRow) => 'error' | 'warning' | undefined;
    /** Смена значения перечитывает данные (после действий над строкой). */
    refreshToken?: number;
    selectable?: boolean;
    onSelectionChange?: (keys: string[], rows: DataGridRow[]) => void;
    /** Выбрать строки извне (итог групповой операции — «Выделить неудачные»); новый `seq` — новый запрос. */
    selectionRequest?: { keys: string[]; seq: number };
    /** Класс обёртки: страницы задают ей место в своей раскладке. */
    className?: string;
    /** Предел высоты таблицы — со скроллом внутри неё. */
    maxHeight?: number | string;
    /** Туллбар и шапка колонок прилипают к верху прокрутки страницы. */
    stickyHeader?: boolean;
    /** Таблица занимает всю высоту контейнера: скроллится только тело. */
    fillHeight?: boolean;
    /** Кнопка «На весь экран»: для списков внутри карточки документа, которым тесно в её
     *  вкладке. Не включать в модалках и drawer — их transform перехватывает position: fixed. */
    allowFullscreen?: boolean;
    /** Заголовок в шапке полноэкранного режима. */
    fullscreenTitle?: ReactNode;
    fullscreenContext?: string;
    /** Первый результат таблицы готов к показу (включая пустой список/ошибку). */
    onInitialReady?: () => void;
    /** Сохранённые виды списка: без сервиса кнопка «Состояния» не показывается. */
    stateService?: IDataGridStateService;
    /** Стартовая сортировка/фильтры/группировка из адреса страницы. */
    initialTableState?: IDataTableSavedState;
    /** Сохранённый вид, который был активен в снимке из адреса страницы. */
    initialViewName?: string;
    /** Ручное переключение сохранённого вида. */
    onViewNameChange?: (name: string | undefined, resetScroll?: boolean) => void;
    /** Изменение табличного состояния для синхронизации с адресом страницы. */
    onTableStateChange?: (state: IDataTableSavedState) => void;
    /** Начальное положение внутренней прокрутки из адреса страницы. */
    initialScrollPosition?: IDataTableScrollPosition;
    /** Изменение внутренней прокрутки для синхронизации с адресом страницы. */
    onScrollPositionChange?: (position: IDataTableScrollPosition) => void;
    /** Показать в toolbar переход к абсолютному началу серверного списка. */
    showServerScrollToStart?: boolean;
    /** Задержка перед onTableStateChange; URL-синхронизация использует 0. */
    stateChangeDebounceMs?: number;
    /** Смена значения объявляет серверную выборку другой (страница сменила
     *  предустановленный фильтр): окно строк и прокрутка начинаются с нуля. */
    serverResetToken?: number;
    /** Ключ личных настроек показа (высота строк) — аналог
     *  gridKey старых гридов. По умолчанию ключ списка; задавать там, где список
     *  один и тот же, а dataKey у каждого документа свой (карта маршрута). */
    settingsKey?: string;
    /** Начальная высота строк, если пользователь ещё не выбрал свою. */
    defaultWrapMode?: DataTableWrapMode;
    /** Дерево: дочерние строки узла приходят отдельным запросом при раскрытии.
     *  Корни грузит обычный источник, поэтому нужен только загрузчик детей. */
    loadChildren?: (row: DataGridRow, fields: string) => Promise<DataGridRow[]>;
    /** Как адресовать поля в запросе: деревья — именами, плоские списки — позициями. */
    fieldsMode?: 'index' | 'name';
    testId?: string;
}

/**
 * Список на серверных данных: схема колонок приходит с бэкенда, выборка идёт
 * через серверный источник базовой таблицы. Замена `DevExpressDataGridDynamic`
 * (этап N10) — здесь только связывание схемы, загрузчика и грида, табличная
 * механика живёт в `@atoms/DataTable`.
 */
const DataGrid: FC<IDataGridProps> = ({
    dataKey,
    service,
    scheme: externalScheme,
    columnRender,
    linkPrefix,
    wordWrapClassName,
    readOnly,
    cellTemplates,
    toolbarStart,
    toolbarEnd,
    schemeDebug,
    footerEnd,
    rowActions,
    rowActionsWidth = ACTIONS_COLUMN_WIDTH,
    rowClassName,
    rowStatus,
    onRowClick,
    onOpenRowLink,
    refreshToken = 0,
    selectable,
    onSelectionChange,
    selectionRequest,
    className,
    maxHeight,
    stickyHeader,
    fillHeight,
    allowFullscreen,
    fullscreenTitle,
    fullscreenContext,
    onInitialReady,
    stateService,
    initialTableState: initialTableStateProp,
    initialViewName: initialViewNameProp,
    onViewNameChange,
    onTableStateChange,
    initialScrollPosition: initialScrollPositionProp,
    onScrollPositionChange,
    showServerScrollToStart,
    stateChangeDebounceMs,
    serverResetToken,
    settingsKey,
    defaultWrapMode,
    loadChildren,
    fieldsMode = 'index',
    testId,
}) => {
    // Восстановленное из URL состояние — снимок момента открытия: читаем один раз
    // на маунт, чтобы эффекты не зависели от стабильности ссылок у потребителя.
    const { current: initial } = useRef({
        tableState: initialTableStateProp,
        viewName: initialViewNameProp,
        scrollPosition: initialScrollPositionProp,
    });
    // Сохранённые виды: список из сервиса, выбранный вид задаёт стартовое
    // состояние таблицы, а текущее состояние — снимок для сохранения.
    const [views, setViews] = useState<IGridStateSettings[] | undefined>(undefined);
    const viewNameChangeRef = useRef(onViewNameChange);
    useEffect(() => {
        viewNameChangeRef.current = onViewNameChange;
    }, [onViewNameChange]);
    /** URL-снимок применяется до первого ручного выбора вида или смены схемы:
     *  после них таблицей распоряжаются уже сами виды и схема. */
    const urlSnapshotDoneRef = useRef(false);
    const externalSchemeColumnsRef = useRef<string | null>(null);
    const [viewsOpen, setViewsOpen] = useState(false);
    const [viewApplyEpoch, setViewApplyEpoch] = useState(0);
    const [, refreshStateDetails] = useState(0);
    // Поколение таблицы, чьё состояние читает модалка. В рендере применения вида
    // tableRef ещё указывает на размонтируемую таблицу с прежними отличиями.
    const [stateEpoch, setStateEpoch] = useState(viewApplyEpoch);
    useEffect(() => setStateEpoch(viewApplyEpoch), [viewApplyEpoch]);
    const tableRef = useRef<IDataTableHandle<DataGridRow>>(null);
    // Режим «на весь экран» живёт здесь, а не только в таблице: смена сохранённого вида
    // пересоздаёт её по key, и без этого флага выбор вида выбрасывал бы из режима. Высоту
    // обёртки держим сами — у рождённой развёрнутой таблицы своей заглушки-высоты нет.
    const swapRef = useRef<HTMLDivElement>(null);
    const [fullscreen, setFullscreen] = useState<{ active: boolean; minHeight?: number }>({ active: false });
    const handleFullscreenChange = useCallback((active: boolean) => {
        setFullscreen(active ? { active, minHeight: swapRef.current?.offsetHeight } : { active });
    }, []);
    // вкладку карточки сменили мимо слоя (история браузера) — развёрнутый список в ней не оставляем
    const { activeTab } = useContext(TabsContext);
    useEffect(() => {
        tableRef.current?.exitFullscreen();
    }, [activeTab]);
    /** Сколько строк выбрано: при выборе тулбар показывает островок групповых действий. */
    const [selectedCount, setSelectedCount] = useState(0);
    useEffect(() => {
        if (selectionRequest) tableRef.current?.selectRows(selectionRequest.keys);
    }, [selectionRequest]);
    const tableStateRef = useRef<IDataTableSavedState>({});
    const [loadedScheme, setLoadedScheme] = useState<IGridDescriptionResponse | null>(null);
    const [schemeError, setSchemeError] = useState<unknown>(undefined);
    useEffect(() => {
        if (schemeError) onInitialReady?.();
    }, [schemeError, onInitialReady]);
    /** «Повторить» после отказа схемы: тот же запрос ещё раз. */
    const [schemeAttempt, setSchemeAttempt] = useState(0);
    const external = externalScheme !== undefined;
    const scheme = external ? (externalScheme ?? null) : loadedScheme;

    useEffect(() => {
        if (external) return;
        let cancelled = false;
        setLoadedScheme(null);
        setSchemeError(undefined);
        setVisibilityOverride(null);
        service
            .fetchScheme(dataKey)
            .then((res) => {
                if (!cancelled) setLoadedScheme(res.data);
            })
            .catch((error: unknown) => {
                if (!cancelled) setSchemeError(error ?? new Error('scheme'));
            });
        return () => {
            cancelled = true;
        };
    }, [dataKey, service, external, schemeAttempt]);

    // Смена внешней схемы — это другой фильтр папки: и набор колонок, и выборка
    // на сервере другие, поэтому сбрасываем видимость и перечитываем данные.
    const [schemeVersion, setSchemeVersion] = useState(0);
    const prevExternalRef = useRef(externalScheme);
    useEffect(() => {
        if (!external || prevExternalRef.current === externalScheme) return;
        prevExternalRef.current = externalScheme;
        tableRef.current?.clearSelection();
        const columnsSignature = externalScheme
            ? JSON.stringify((externalScheme.columns ?? []).map((column, index) => dataGridColumnId(column, index)))
            : null;
        const previousColumnsSignature = externalSchemeColumnsRef.current;
        const firstUsableScheme = previousColumnsSignature == null && columnsSignature != null;
        const columnsChanged = previousColumnsSignature != null && previousColumnsSignature !== columnsSignature;
        externalSchemeColumnsRef.current = columnsSignature;
        // Другой состав колонок — другая папка или другой фильтр: пользовательская
        // видимость и URL-снимок к ним не относятся. Первая схема и повторный
        // одинаковый ответ (в dev StrictMode их два) видимость не трогают —
        // иначе стиралась бы восстановленная из URL в момент снятия скелетона.
        if (columnsChanged) {
            setVisibilityOverride(null);
            urlSnapshotDoneRef.current = true;
        }
        // Первая схема — не перечитывание: первый запрос таблица делает сама,
        // а бамп сразу после маунта дёргал бы лишний reload в dev StrictMode.
        if (!firstUsableScheme) setSchemeVersion((version) => version + 1);
    }, [external, externalScheme]);

    useEffect(() => {
        if (!stateService) return;
        let cancelled = false;
        // смена папки: старые виды больше не действуют — иначе их видимость колонок
        // и вид по умолчанию переносились бы на новый список
        setViews(undefined);
        setVisibilityOverride(null);
        stateService
            .fetchState(dataKey)
            .then((res) => {
                // пустое хранилище отдаёт не массив, а пустую строку
                if (cancelled) return;
                const loadedViews = Array.isArray(res.data) ? res.data : [];
                const urlView = initial.viewName
                    ? loadedViews.find((view) => view.name === initial.viewName)
                    : undefined;
                // Вид из ссылки удалён, переименован или принадлежит другому
                // пользователю: снимок из URL остаётся без основы, а без снимка
                // открывается вид по умолчанию. Имя в URL не должно висеть.
                const activeView =
                    urlView ??
                    (initial.viewName && initial.tableState ? undefined : loadedViews.find((view) => view.default));
                setViews(loadedViews.map((view) => ({ ...view, selected: view === activeView })));
                if (activeView !== urlView || (initial.viewName && !urlView))
                    viewNameChangeRef.current?.(activeView?.name, false);
            })
            .catch((error: unknown) => {
                if (cancelled) return;
                // список остаётся рабочим, теряются только сохранённые виды —
                // об этом говорим уведомлением, а не сообщением на месте таблицы
                setViews([]);
                sendNotification({
                    message: 'Сохранённые состояния таблицы не загрузились. Список открыт в стандартном виде.',
                    traceId: errorTraceId(error),
                    variant: 'red',
                });
            });
        return () => {
            cancelled = true;
        };
    }, [stateService, dataKey, initial]);

    const selectedView = useMemo(() => views?.find((view) => view.selected), [views]);
    const viewState = useMemo(() => viewToTableState(selectedView), [selectedView]);
    const useInitialUrlSnapshot = !urlSnapshotDoneRef.current;

    const columns = useMemo(() => {
        if (!scheme) return [];
        const byField = new Map(
            (scheme.columns ?? []).flatMap((column, index) => {
                const field = dataGridColumnId(column, index);
                return field ? [[field, column] as const] : [];
            }),
        );
        const deps: IDataGridCellDeps = {
            linkPrefix,
            urlEnabled: scheme.options?.urlEnabled,
            wrapClassName: wordWrapClassName,
            readOnly,
            cellTemplates,
        };
        const built = buildDataGridColumns(scheme.columns ?? []).map((column) =>
            applyDataGridCellRender(column, byField.get(column.field), deps),
        );
        return columnRender ? built.map(columnRender) : built;
    }, [scheme, columnRender, linkPrefix, wordWrapClassName, readOnly, cellTemplates]);

    const options = useMemo(() => (scheme ? buildDataGridOptions(scheme) : null), [scheme]);
    const defaultSort = useMemo(() => (scheme ? buildDataGridSort(scheme.columns ?? []) : []), [scheme]);
    const linkedRowHref = useCallback(
        (row: DataGridRow) =>
            linkedDocHrefOf(row, {
                linkPrefix,
                urlEnabled: scheme?.options?.urlEnabled,
            }),
        [linkPrefix, scheme?.options?.urlEnabled],
    );
    const openLinkedRow = useCallback(
        (row: DataGridRow) => {
            const href = linkedRowHref(row);
            if (href) onOpenRowLink?.(href, row);
        },
        [linkedRowHref, onOpenRowLink],
    );
    const canOpenLinkedRows = !!onOpenRowLink && !!linkPrefix && !!scheme?.options?.urlEnabled;
    const effectiveRowClick = onRowClick ?? (canOpenLinkedRows ? openLinkedRow : undefined);
    const isEffectiveRowClickable = onRowClick
        ? undefined
        : canOpenLinkedRows
          ? (row: DataGridRow) => !!linkedRowHref(row)
          : undefined;

    // Дефолты схемы для URL-снимка: против них снимок хранится дельтой,
    // по ним же разворачиваются индексы колонок и дельта видимости.
    const urlSchema = useMemo<IGridUrlSchema>(
        () => ({
            columnIds: columns.map((column) => column.field),
            hiddenColumnIds: columns.filter((column) => column.visible === false).map((column) => column.field),
            pinnedLeftIds: columns.filter((column) => column.pinned === 'left').map((column) => column.field),
            pinnedRightIds: columns.filter((column) => column.pinned === 'right').map((column) => column.field),
            defaultSorting: defaultSort.map((sort) => ({ id: sort.field, desc: sort.dir === 'desc' })),
            defaultPageSize: options?.pageSize,
        }),
        [columns, defaultSort, options],
    );

    const initialTableState = useMemo(
        () => restoreGridTableUrlState(initial.tableState, urlSchema),
        [initial, urlSchema],
    );

    const tableInitialState = useMemo(() => {
        if (!viewState && !initialTableState) return undefined;
        const known = new Set(columns.map((column) => column.field));
        const restored =
            useInitialUrlSnapshot && initialTableState
                ? {
                      ...initialTableState,
                      sorting: initialTableState.sorting?.filter((item) => known.has(item.id)),
                      columnFilters: initialTableState.columnFilters?.filter((item) => known.has(item.id)),
                      grouping: initialTableState.grouping?.filter((field) => known.has(field)),
                  }
                : undefined;
        return {
            ...viewState,
            ...restored,
            // Видимость серверных колонок контролируется DataGrid: от неё зависит
            // состав fields в запросе, поэтому initialState ею не распоряжается.
            columnVisibility: undefined,
        };
    }, [columns, initialTableState, useInitialUrlSnapshot, viewState]);

    // Видимостью колонок распоряжается организм, а не таблица: от неё зависит
    // состав запрашиваемых полей — бэкенд отдаёт значения только запрошенных.
    // До первого действия пользователя видимость выводится из схемы прямо в
    // рендере, иначе первый запрос ушёл бы с пустым набором полей.
    const [visibilityOverride, setVisibilityOverride] = useState<Record<string, boolean> | null>(null);
    const columnVisibility = useMemo(() => {
        if (visibilityOverride) return visibilityOverride;
        const hidden: Record<string, boolean> = {};
        for (const c of columns) if (c.visible === false) hidden[c.field] = false;
        // До первого действия пользователя: URL-снимок точнее сохранённого вида,
        // вид точнее схемы. После ручного выбора вида URL-снимок не применяется.
        // Снимок хранит видимость дельтой против схемы: её отсутствие значит
        // «как в схеме», а не «как в виде» — иначе показанная поверх вида
        // колонка снова пряталась бы после F5.
        if (useInitialUrlSnapshot && initialTableState) return initialTableState.columnVisibility ?? hidden;
        return viewState?.columnVisibility ?? hidden;
    }, [columns, initialTableState, useInitialUrlSnapshot, viewState?.columnVisibility, visibilityOverride]);

    const fieldsParam = useMemo(() => {
        const visible = new Set(columns.filter((c) => columnVisibility[c.field] !== false).map((c) => c.field));
        return buildFieldsParam(scheme?.columns ?? [], visible, fieldsMode);
    }, [columns, columnVisibility, scheme, fieldsMode]);

    // Скрытие колонки сужает набор полей: данные для оставшихся уже загружены,
    // перезапрашивать нечего — иначе подгруженные прокруткой строки схлопывались
    // бы до первой порции. Показ ранее скрытой колонки, наоборот, требует новых
    // значений, и тогда выборка перечитывается (в режиме прокрутки — целиком,
    // на весь накопленный объём, см. `serverReloadKeepsCount`).
    const requestedFieldsRef = useRef<string>('');
    const fieldsGrew = useMemo(() => {
        const prev = new Set(requestedFieldsRef.current.split(',').filter(Boolean));
        return fieldsParam
            .split(',')
            .filter(Boolean)
            .some((index) => !prev.has(index));
    }, [fieldsParam]);
    if (fieldsGrew) requestedFieldsRef.current = fieldsParam;

    // Внешний сигнал, смена схемы и расширение набора полей ведут один монотонный
    // счётчик: у таблицы для перезапроса один вход, а независимые числа в нём
    // складывались бы в одинаковые суммы.
    const [reloadEpoch, setReloadEpoch] = useState(0);
    const reloadSignal = `${requestedFieldsRef.current}|${refreshToken}|${schemeVersion}`;
    const prevSignalRef = useRef<string | null>(null);
    useEffect(() => {
        // появление полей после загрузки схемы — не перезапрос: первый запрос
        // таблица делает сама, а до схемы запрашивать было нечего
        const hadFields = !!prevSignalRef.current && !prevSignalRef.current.startsWith('|');
        if (hadFields && prevSignalRef.current !== reloadSignal) {
            setReloadEpoch((epoch) => epoch + 1);
        }
        prevSignalRef.current = reloadSignal;
    }, [reloadSignal]);

    const fieldsRef = useRef(fieldsParam);
    fieldsRef.current = fieldsParam;
    const schemeColumnsRef = useRef(scheme?.columns ?? []);
    schemeColumnsRef.current = scheme?.columns ?? [];

    // Источник один на список: поля и колонки схемы он читает на каждый запрос, поэтому смена вида, видимости
    // или схемы выборку другой не объявляет — перечитывание идёт через reloadEpoch.
    const reconcileSelectionRef = useRef<((result: IServerLoadResult<DataGridRow>) => void) | undefined>(undefined);
    const gridSource = useMemo(
        () =>
            createDataGridSource({
                service,
                dataKey,
                fields: () => fieldsRef.current,
                columns: () => schemeColumnsRef.current,
                onRootLoad: () => reconcileSelectionRef.current,
            }),
        [service, dataKey],
    );
    const previousRefreshRef = useRef({ gridSource, refreshToken });
    useEffect(() => {
        const previous = previousRefreshRef.current;
        previousRefreshRef.current = { gridSource, refreshToken };
        if (previous.gridSource !== gridSource || previous.refreshToken === refreshToken) return;
        const selected = new Set(tableRef.current?.getSelected().keys);
        // В дереве ключи выборки — только корни, дети приходят раскрытием: сверка сняла бы выбранных детей.
        if (!selected.size || loadChildren) return;
        let requestController: AbortController | undefined;
        // Действие могло убрать часть строк из папки. Выбранные строки из ответа первой загрузки после него
        // остаются без запроса; про остальные (за окном, скрытые фильтром колонки, а в группировке — все)
        // спрашиваем ключи всей папки без фильтров таблицы: выбор снимаем только у строк, которых в ней нет.
        const reconcile = (result: IServerLoadResult<DataGridRow>) => {
            if (reconcileSelectionRef.current !== reconcile || !fieldsRef.current) return;
            requestController?.abort();
            const loaded = new Set(result.rows.map((row) => row.key));
            if ([...selected].every((key) => loaded.has(key))) {
                reconcileSelectionRef.current = undefined;
                return;
            }
            const controller = new AbortController();
            requestController = controller;
            void gridSource
                .keys({ skip: 0, take: 0, requireTotalCount: true }, controller.signal)
                .then((keys) => {
                    const table = tableRef.current;
                    if (controller.signal.aborted || !table) return;
                    reconcileSelectionRef.current = undefined;
                    const existing = new Set(keys);
                    const current = table.getSelected().keys;
                    // Изменения выбора, сделанные пользователем во время запроса, не перетираем.
                    const remaining = current.filter((key) => !selected.has(key) || existing.has(key));
                    if (remaining.length === current.length) return;
                    if (remaining.length) table.selectRows(remaining);
                    else table.clearSelection();
                })
                .catch((error: unknown) => {
                    if (controller.signal.aborted) return;
                    reconcileSelectionRef.current = undefined;
                    sendNotification({
                        message: `Не удалось обновить выбор после действия — выбор оставлен прежним. ${errorText(error)}`,
                        traceId: errorTraceId(error),
                        variant: 'red',
                    });
                });
        };
        reconcileSelectionRef.current = reconcile;
        return () => {
            requestController?.abort();
            reconcileSelectionRef.current = undefined;
        };
        // serverResetToken и viewApplyEpoch: другая выборка или новая таблица — выбор снят, ждущая сверка не нужна.
    }, [gridSource, refreshToken, serverResetToken, viewApplyEpoch, loadChildren]);
    const allowExport = !!options?.allowExport;
    // Фоновую выгрузку включает схема: объекта нет — фон на сервере выключен, у таблицы только обычная выгрузка.
    const backgroundExportOffered = !!scheme?.options?.backgroundExport;
    const suggestAboveRows = scheme?.options?.backgroundExport?.suggestAboveRows ?? undefined;
    const source = useMemo(() => {
        const { exportInBackground, ...rest } = gridSource;
        return {
            ...rest,
            export: allowExport ? gridSource.export : undefined,
            backgroundExport:
                allowExport && backgroundExportOffered && exportInBackground
                    ? { suggestAboveRows, start: exportInBackground }
                    : undefined,
            refreshToken: reloadEpoch,
        };
    }, [gridSource, allowExport, backgroundExportOffered, suggestAboveRows, reloadEpoch]);

    // Пользовательские скрипты строки и ячейки из конфигурации: файлы грузятся по
    // ссылке из схемы и вызываются на каждую отрисованную строку/ячейку. Контекст
    // повторяет событие DX (rowElement/cellElement, data, key, column), чтобы уже
    // написанные конфигурационные скрипты работали без правок.
    const { onRowPreparedScript, onCellPreparedScript } = useUserScripts(
        scheme?.options?.onRowPrepared,
        scheme?.options?.onCellPrepared,
    );

    const columnHeaderByField = useMemo(
        () =>
            new Map(
                (scheme?.columns ?? []).flatMap((column, index) => {
                    const field = dataGridColumnId(column, index);
                    return field ? [[field, column.header] as const] : [];
                }),
            ),
        [scheme],
    );
    // Классы колонки из схемы (cssClasses) — контракт стилей конфигураций: на них
    // завязаны «светофоры» строк и ячеек в custom.css. DX вешал их сам, здесь
    // добавляем ячейке при рендере.
    const columnClassesByField = useMemo(
        () =>
            new Map(
                (scheme?.columns ?? []).flatMap((column, index) => {
                    if (!column.cssClasses?.length) return [];
                    const field = dataGridColumnId(column, index);
                    return field ? [[field, column.cssClasses] as const] : [];
                }),
            ),
        [scheme],
    );

    const onRowRendered = useCallback(
        ({ element, row, rowIndex }: { element: HTMLElement; row: DataGridRow; rowIndex: number }) => {
            try {
                // element — как у DX-события: контейнер, чей firstChild несёт
                // data-filter (шипованные скрипты читают ключ выбранного фильтра
                // через row.element.firstChild). Обёртку с data-filter даёт
                // страница (search-folder/tasks-folder), грид о ней не знает.
                const filterWrap = element.closest('[data-filter]');
                const container = filterWrap?.parentElement ?? element.closest('.dgn-swap')?.parentElement ?? undefined;
                onRowPreparedScript?.(
                    { rowElement: element, data: row, key: row.key, rowIndex, rowType: 'data', element: container },
                    dataKey,
                );
            } catch (error) {
                console.error('Скрипт строки списка завершился ошибкой', error);
            }
        },
        [onRowPreparedScript, dataKey],
    );

    const onCellRendered = useCallback(
        ({
            element,
            row,
            field,
            value,
            text,
        }: {
            element: HTMLElement;
            row: DataGridRow;
            field: string;
            value: unknown;
            text: string;
        }) => {
            // значение ячейки в data-value: на него завязаны стили конфигураций
            // (тот же контракт, что давал setDataValueAttr у DX-гридов)
            if (!element.getAttribute('data-value')) element.setAttribute('data-value', text);
            const columnClasses = columnClassesByField.get(field);
            if (columnClasses) element.classList.add(...columnClasses);
            try {
                onCellPreparedScript?.(
                    {
                        cellElement: element,
                        data: row,
                        key: row.key,
                        rowType: 'data',
                        // конфигурационные скрипты адресуют колонку тремя способами:
                        // dataField/name (поле) и caption (заголовок) — даём все
                        column: {
                            dataField: field,
                            name: field,
                            caption: columnHeaderByField.get(field) ?? field,
                        },
                        value,
                        text,
                        displayValue: text,
                    },
                    dataKey,
                );
            } catch (error) {
                console.error('Скрипт ячейки списка завершился ошибкой', error);
            }
        },
        [onCellPreparedScript, dataKey, columnHeaderByField, columnClassesByField],
    );

    // Раскрытие строки формой карточки: шаблон приходит в схеме (SearchForm/DetailsForms),
    // сама форма — тот же компонент, что рисует детали у DX-списка. Вложенного грида
    // деталей бэкенд не отдаёт (GetDetailsGrid везде null), поэтому его тут нет.
    const detailsForm = (scheme as IDataGridDescriptionResponse | null)?.detailsForm;
    const canShowDetails = !!detailsForm && scheme?.options?.isMaster !== false;
    const renderDetail = useCallback(
        (row: DataGridRow) => (
            <DocumentFormDetails docId={getDocIdFromLinkedDocId(row.key)} detailsForm={detailsForm!} />
        ),
        [detailsForm],
    );

    // ----- Дерево: дети узла приходят при раскрытии и живут рядом с корнями -----
    const [childRows, setChildRows] = useState<Map<string, DataGridRow[]>>(() => new Map());
    const [loadingRows, setLoadingRows] = useState<string[]>([]);
    const pendingChildrenRef = useRef(new Set<string>());

    // новая выборка (обновление, смена фильтра или списка) — прежние ветки не её
    useEffect(() => {
        setChildRows(new Map());
        setLoadingRows([]);
        pendingChildrenRef.current.clear();
    }, [dataKey, reloadEpoch]);

    const expandRow = useCallback(
        async (row: DataGridRow) => {
            if (!loadChildren || childRows.has(row.key) || pendingChildrenRef.current.has(row.key)) return;
            pendingChildrenRef.current.add(row.key);
            setLoadingRows([...pendingChildrenRef.current]);
            try {
                const rows = await loadChildren(row, fieldsRef.current);
                setChildRows((prev) => new Map(prev).set(row.key, rows));
            } catch (error: unknown) {
                // ветка осталась свёрнутой: список цел, потеряно только раскрытие
                sendNotification({
                    message: `Вложенные строки не загрузились. ${errorText(error, 'Повторите раскрытие.')}`,
                    traceId: errorTraceId(error),
                    variant: 'red',
                });
            } finally {
                pendingChildrenRef.current.delete(row.key);
                setLoadingRows([...pendingChildrenRef.current]);
            }
        },
        [loadChildren, childRows],
    );

    const getSubRows = useCallback((row: DataGridRow) => childRows.get(row.key), [childRows]);
    const canExpandRow = useCallback((row: DataGridRow) => !!row.expandable, []);

    const captureState = (name: string, previous?: IGridStateSettings) =>
        tableStateToView(name, tableRef.current?.getState() ?? tableStateRef.current, scheme?.columns ?? [], previous);

    const handleTableStateChange = useCallback(
        (next: IDataTableSavedState) => {
            tableStateRef.current = next;
            if (viewsOpen) refreshStateDetails((revision) => revision + 1);
            onTableStateChange?.(compactGridTableUrlStateBySchema(next, urlSchema));
        },
        [onTableStateChange, urlSchema, viewsOpen],
    );

    const handleSelectState = useCallback(
        (next: IGridStateSettings[] | undefined, apply = false) => {
            const nextView = next?.find((view) => view.selected);
            if (apply) {
                // Повторное применение того же имени тоже заменяет URL-снимок и живую таблицу.
                urlSnapshotDoneRef.current = true;
                setVisibilityOverride(viewToTableState(nextView)?.columnVisibility ?? null);
                setViewApplyEpoch((epoch) => epoch + 1);
                setSelectedCount(0);
                onViewNameChange?.(nextView?.name);
            } else if (nextView?.name !== selectedView?.name) {
                // Переименование и удаление основы не сбрасывают пользовательские фильтры.
                setVisibilityOverride(tableRef.current?.getState().columnVisibility ?? null);
                onViewNameChange?.(nextView?.name, false);
            }
            setViews(next);
        },
        [onViewNameChange, selectedView?.name],
    );

    const handleSelectionChange = (keys: string[], rows: DataGridRow[]) => {
        setSelectedCount(keys.length);
        onSelectionChange?.(keys, rows);
    };

    // Пока строки выбраны, кнопки тулбара живут островком со счётчиком и снятием
    // выбора — на месте, а не отдельной полосой над списком.
    const toolbarStartNode =
        toolbarStart && selectedCount > 0 ? (
            <div className="dgn-bulk">
                <span className="dgn-bulk__count">
                    <b>{selectedCount}</b> выбрано
                </span>
                {toolbarStart}
                <button
                    type="button"
                    className="dgn-bulk__clear"
                    title="Снять выделение"
                    onClick={() => tableRef.current?.clearSelection()}
                >
                    <HiXMark aria-hidden />
                    Снять выделение
                </button>
            </div>
        ) : (
            toolbarStart
        );

    if (schemeError)
        return (
            <DataTableFailure
                title="Таблица не открылась"
                hint="Описание списка не пришло с сервера. Проверьте соединение и повторите."
                error={schemeError}
                onRetry={() => setSchemeAttempt((attempt) => attempt + 1)}
            />
        );
    // Скелетон живёт в той же обёртке и с той же раскладкой, что и готовый
    // список: без className/fillHeight потребителя таблица-заглушка не получает
    // flex-растяжения родителя и схлопывается в узкую полоску у левого края.
    // Туллбар и пейджер включены, чтобы каркас загрузки совпадал с тем, что
    // придёт (иначе на месте кнопок и футера пусто, и список «прыгает»).
    if (!scheme || !options || (stateService && views === undefined))
        return (
            <div className={clsx('dgn-swap', className)} data-testid={testId}>
                {/* showTableSettings — только чтобы каркас нарисовал полосу
                    туллбара: живых кнопок в нём нет, их рисует скелетон */}
                <DataTable
                    columns={[]}
                    data={[]}
                    getRowId={() => ''}
                    loading
                    fillHeight={fillHeight}
                    showTableSettings
                />
            </div>
        );

    return (
        // key по ключу списка: смена папки — это смена контекста, и содержимое
        // должно смениться заметно, а не подмениться в тех же строках
        <div
            key={dataKey}
            ref={swapRef}
            className={clsx('dgn-swap', className)}
            style={fullscreen.active ? { minHeight: fullscreen.minHeight } : undefined}
            data-testid={testId}
        >
            <DataTable<DataGridRow>
                controlRef={tableRef}
                allowFullscreen={allowFullscreen}
                fullscreenTitle={fullscreenTitle}
                fullscreenContext={fullscreenContext}
                initialFullscreen={fullscreen.active}
                onFullscreenChange={handleFullscreenChange}
                // выбранный вид задаёт стартовое состояние таблицы, а оно читается
                // при монтировании — переключение вида пересоздаёт таблицу
                key={viewApplyEpoch}
                columns={columns}
                getRowId={(row) => row.key}
                source={source}
                onInitialReady={onInitialReady}
                serverLocateRowPage={gridSource.locateRowPage}
                serverResetToken={serverResetToken}
                // без страниц (карта маршрута, журнал ЕИС) список идёт сплошным
                // и догружается прокруткой — иначе за первой страницей ничего нет
                serverScrollMode={options.scrollMode === 'standard' && options.paging ? 'paged' : 'infinite'}
                showServerScrollToStart={showServerScrollToStart}
                serverReloadKeepsCount
                // группировка списков — серверная (страницы свёрнутых групп,
                // содержимое лениво), как у старого грида с groupPaging
                serverGrouping
                defaultSort={defaultSort}
                columnWidthMode={options.columnWidthMode}
                defaultPageSize={options.pageSize}
                pageSizes={options.pageSizes}
                paging={options.paging}
                emptyText={options.emptyText}
                showFilterRow={options.showFilterRow}
                // кнопка-воронка только там, где схема разрешает фильтры строкой
                filterRowToggle={options.showFilterRow}
                showColumnChooser={options.allowColumnChooser}
                showTableSettings
                serverExportLimitModal={scheme?.options?.showExportLimitModal}
                // высота строк — личная настройка на список,
                // как grid.settings.<gridKey> у старых гридов
                wrapModeKey={settingsKey ?? dataKey}
                defaultWrapMode={defaultWrapMode}
                showHeaderFilter
                selectable={selectable ?? options.selectable}
                selectAllMode={options.selectAllMode}
                showSelectAll={options.showSelectAll}
                recursiveSelection={options.recursiveSelection}
                onSelectionChange={handleSelectionChange}
                showGroupPanel={options.showGroupPanel}
                renderDetail={canShowDetails ? renderDetail : undefined}
                onRowRendered={onRowPreparedScript ? onRowRendered : undefined}
                onCellRendered={onCellRendered}
                getSubRows={loadChildren ? getSubRows : undefined}
                canExpandRow={loadChildren ? canExpandRow : undefined}
                onExpandRow={loadChildren ? expandRow : undefined}
                loadingRowIds={loadingRows}
                maxHeight={maxHeight}
                stickyHeader={stickyHeader}
                fillHeight={fillHeight}
                cacheRows
                // видимость колонок ведёт организм (см. columnVisibility), из вида
                // в таблицу уходят только ширины, порядок, сортировка и размер страницы
                initialState={tableInitialState}
                onStateChange={handleTableStateChange}
                stateChangeDebounceMs={stateChangeDebounceMs}
                initialScrollPosition={useInitialUrlSnapshot ? initial.scrollPosition : undefined}
                onScrollPositionChange={onScrollPositionChange}
                toolbarStart={toolbarStartNode}
                footerEnd={footerEnd}
                toolbarEnd={
                    <>
                        {toolbarEnd}
                        {schemeDebug && (
                            <TableSchemeDebugButton
                                {...schemeDebug}
                                buttonClassName="dtb__icon-btn"
                                columns={toSchemeDebugColumns(scheme.columns ?? [])}
                                details={schemeDebugDetails(scheme)}
                            />
                        )}
                        {stateService && (
                            <GridStateButton
                                stateName={selectedView?.name}
                                opened={viewsOpen}
                                onClick={() => setViewsOpen(true)}
                            />
                        )}
                    </>
                }
                rowActions={rowActions}
                rowClassName={rowClassName}
                rowStatus={rowStatus}
                onRowClick={effectiveRowClick}
                isRowClickable={isEffectiveRowClickable}
                rowActionsPosition="right"
                rowActionsAlwaysVisible
                rowActionsWidth={rowActionsWidth}
                columnVisibility={columnVisibility}
                // мержим: таблица присылает только то, что знает сама, и полный
                // перезапись стирала бы скрытые видом колонки
                onColumnVisibilityChange={(next) => setVisibilityOverride((prev) => ({ ...prev, ...next }))}
            />
            {stateService && (
                <StateModal
                    opened={viewsOpen}
                    stateKey={dataKey}
                    stateService={stateService}
                    stateSettings={views}
                    changes={
                        viewsOpen && stateEpoch === viewApplyEpoch
                            ? gridStateChanges(
                                  selectedView,
                                  tableRef.current?.getState() ?? tableStateRef.current,
                                  columns,
                                  options.pageSize,
                              )
                            : []
                    }
                    captureState={captureState}
                    onSelectState={handleSelectState}
                    onRefreshGrid={() => setReloadEpoch((epoch) => epoch + 1)}
                    onClose={() => setViewsOpen(false)}
                />
            )}
        </div>
    );
};

export default DataGrid;
